/**
 * MasterDocProps — self-contained sectioned key-value properties panel.
 *
 * FULLY MANAGES ITS OWN STATE. Only resets on doc_id change.
 * Sections work like document sub-tabs. "Properties" is always first.
 * Types: text, number, date, url, checkbox, color, font
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, ChevronDown, FileText, Settings2, X, GripVertical, Trash2 } from "lucide-react";

export const PROP_TYPES = ["text", "number", "date", "url", "checkbox", "color", "font", "standard"];
const DEFAULT_SECTION = "Properties";

const TYPE_DOT = { standard: "#F59E0B",
  date: "#5A90F5", number: "#14B8A6", url: "#A855F7",
  checkbox: "#22C55E", text: "#52525B", color: "#EC4899", font: "#F97316",
};

const FONT_OPTIONS = [
  "inherit", "Arial", "Georgia", "Helvetica", "Impact", "Courier New",
  "Times New Roman", "Trebuchet MS", "Verdana", "DM Sans", "IBM Plex Mono",
];


// ── StandardCell — ordered list editor for "standard" type ───────────────────
// Stores JSON: [{id, text, children:[{id,text}]}]
function StandardCell({ initialValue, onCommit }) {
  const parse = (v) => {
    try { const p = JSON.parse(v || "[]"); return Array.isArray(p) ? p : []; } catch { return []; }
  };
  const [items, setItems] = useState(() => parse(initialValue));
  const [newText, setNewText] = useState("");
  const dragIdx = useRef(null);

  const save = useCallback((next) => {
    setItems(next);
    onCommit?.(JSON.stringify(next));
  }, [onCommit]);

  const addItem = () => {
    const txt = newText.trim();
    if (!txt) return;
    save([...items, { id: Date.now().toString(), text: txt, children: [] }]);
    setNewText("");
  };

  const updateText = (id, text) => save(items.map(it => it.id === id ? { ...it, text } : it));
  const removeItem = (id) => save(items.filter(it => it.id !== id));

  const onDragStart = (e, idx) => { dragIdx.current = idx; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === idx) return;
    const next = [...items];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(idx, 0, moved);
    dragIdx.current = idx;
    save(next);
  };
  const onDragEnd = () => { dragIdx.current = null; };

  return (
    <div className="w-full space-y-1">
      {items.map((item, idx) => (
        <div key={item.id}
          draggable
          onDragStart={e => onDragStart(e, idx)}
          onDragOver={e => onDragOver(e, idx)}
          onDragEnd={onDragEnd}
          className="flex items-center gap-1 group bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1">
          <GripVertical className="w-3 h-3 text-[#3A3A3A] group-hover:text-[#52525B] cursor-grab flex-shrink-0" />
          <span className="text-[10px] text-[#52525B] w-4 text-center flex-shrink-0">{idx + 1}.</span>
          <input
            value={item.text}
            onChange={e => updateText(item.id, e.target.value)}
            onBlur={e => updateText(item.id, e.target.value)}
            className="flex-1 bg-transparent text-xs text-[#EDEDED] outline-none min-w-0"
          />
          <button onClick={() => removeItem(item.id)}
            className="opacity-0 group-hover:opacity-100 text-[#52525B] hover:text-[#FF3B30] transition-opacity flex-shrink-0">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      <div className="flex gap-1">
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Add item…"
          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] placeholder-[#3F3F46] outline-none focus:border-[#5A90F5]"
        />
        <button onClick={addItem}
          className="px-2 py-1 rounded bg-[#1a1a1a] border border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] text-xs">+</button>
      </div>
    </div>
  );
}

// ── Value input cells per type ────────────────────────────────────────────────
function ValueCell({ initialValue, propType, onCommit }) {
  const [on, setOn] = useState(initialValue === "true");
  if (propType === "standard") {
    return <StandardCell initialValue={initialValue} onCommit={onCommit} />;
  }
  if (propType === "checkbox") {
    return (
      <button type="button"
        onClick={() => { const v = !on; setOn(v); onCommit(v ? "true" : "false"); }}
        className={`w-4 h-4 rounded border flex-shrink-0 ${on ? "bg-[#5A90F5] border-[#5A90F5]" : "border-[#3F3F46] hover:border-[#5A90F5]"}`}>
        {on && <svg viewBox="0 0 10 10" className="w-full h-full p-0.5" fill="none">
          <path d="M2 5l2.5 2.5 4-4" stroke="#0A0A0A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>}
      </button>
    );
  }
  if (propType === "color") {
    return (
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <input type="color" defaultValue={initialValue || "#5A90F5"}
          onBlur={e => onCommit(e.target.value)}
          onChange={e => onCommit(e.target.value)}
          className="w-6 h-5 rounded cursor-pointer border-0 bg-transparent p-0 flex-shrink-0" />
        <input type="text" defaultValue={initialValue || ""}
          placeholder="#000000"
          onBlur={e => { if (e.target.value !== initialValue) onCommit(e.target.value); }}
          onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="flex-1 bg-transparent text-[12px] text-[#EDEDED] outline-none min-w-0 font-mono" />
      </div>
    );
  }
  if (propType === "font") {
    return (
      <div className="relative flex-1 min-w-0">
        <select defaultValue={initialValue || "inherit"}
          onChange={e => onCommit(e.target.value)}
          style={{ fontFamily: initialValue || "inherit" }}
          className="w-full bg-transparent text-[12px] text-[#EDEDED] outline-none cursor-pointer appearance-none pr-4">
          {FONT_OPTIONS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f === "inherit" ? "Default" : f}</option>)}
        </select>
        <ChevronDown className="w-2.5 h-2.5 absolute right-0 top-1/2 -translate-y-1/2 text-[#3F3F46] pointer-events-none" />
      </div>
    );
  }
  return (
    <input
      type={propType === "date" ? "date" : propType === "number" ? "number" : propType === "url" ? "url" : "text"}
      defaultValue={initialValue || ""}
      onBlur={e => { if (e.target.value !== (initialValue || "")) onCommit(e.target.value); }}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className="w-full bg-transparent text-[12px] text-[#EDEDED] outline-none min-w-0"
    />
  );
}

// ── New-row value input (controlled so user can type before committing) ────────
function NewValueInput({ propType, value, onChange }) {
  if (propType === "standard") {
    return <StandardCell initialValue={value} onCommit={onChange} />;
  }
  if (propType === "checkbox") {
    return (
      <button type="button" onClick={() => onChange(value === "true" ? "false" : "true")}
        className={`w-4 h-4 rounded border flex-shrink-0 ${value === "true" ? "bg-[#5A90F5] border-[#5A90F5]" : "border-[#3F3F46]"}`}>
        {value === "true" && <svg viewBox="0 0 10 10" className="w-full h-full p-0.5" fill="none">
          <path d="M2 5l2.5 2.5 4-4" stroke="#0A0A0A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>}
      </button>
    );
  }
  if (propType === "color") {
    return (
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <input type="color" value={value || "#5A90F5"} onChange={e => onChange(e.target.value)}
          className="w-6 h-5 rounded cursor-pointer border-0 bg-transparent p-0 flex-shrink-0" />
        <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 bg-transparent text-[12px] text-[#EDEDED] outline-none min-w-0 font-mono" />
      </div>
    );
  }
  if (propType === "font") {
    return (
      <select value={value || "inherit"} onChange={e => onChange(e.target.value)}
        className="w-full bg-transparent text-[12px] text-[#EDEDED] outline-none cursor-pointer appearance-none">
        {FONT_OPTIONS.map(f => <option key={f} value={f}>{f === "inherit" ? "Default" : f}</option>)}
      </select>
    );
  }
  return (
    <input type={propType === "number" ? "number" : propType === "date" ? "date" : "text"}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder="Value…"
      className="flex-1 bg-transparent text-[12px] text-[#EDEDED] outline-none min-w-0 placeholder-[#3F3F46]" />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MasterDocProps({ doc, properties: initialProps = [], onChange, onContextMenu }) {
  const [props, setProps] = useState(initialProps);
  const docIdRef = useRef(doc?.doc_id);
  const propsRef = useRef(props);
  propsRef.current = props; // keep in sync with latest state

  useEffect(() => {
    if (doc?.doc_id !== docIdRef.current) {
      docIdRef.current = doc?.doc_id;
      setProps(doc?.properties || []);
    }
  }, [doc?.doc_id]);

  const sectionNames = [DEFAULT_SECTION, ...Array.from(
    new Set(props.map(p => p._section).filter(s => s && s !== DEFAULT_SECTION))
  )];
  const [activeSection, setActiveSection] = useState(DEFAULT_SECTION);
  const [renamingSection, setRenamingSection] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newType, setNewType] = useState("text");
  const [newValue, setNewValue] = useState("");
  const keyRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange; // always latest

  const save = (nextProps) => { setProps(nextProps); onChange?.(nextProps); };

  const sectionItems = props.filter(p => (p._section || DEFAULT_SECTION) === activeSection);

  const findFullIdx = (sectionIdx) => {
    let count = -1;
    return props.findIndex(p => {
      if ((p._section || DEFAULT_SECTION) === activeSection) count++;
      return count === sectionIdx;
    });
  };

  const setVal = (sectionIdx, patch) => {
    const fullIdx = findFullIdx(sectionIdx);
    if (fullIdx === -1) return;
    save(props.map((p, i) => i === fullIdx ? { ...p, ...patch } : p));
  };

  const remove = (sectionIdx) => {
    const fullIdx = findFullIdx(sectionIdx);
    if (fullIdx !== -1) save(props.filter((_, i) => i !== fullIdx));
  };

  const commit = () => {
    const k = newKey.trim();
    if (!k) { setAdding(false); return; }
    save([...props, { key: k, value: newValue, type: newType, _section: activeSection }]);
    setNewKey(""); setNewType("text"); setNewValue("");
    setTimeout(() => keyRef.current?.focus(), 0);
  };

  const addSection = () => {
    const name = `Section ${sectionNames.length}`;
    setActiveSection(name);
    save([...props, { key: "__placeholder__", value: "", type: "text", _section: name, _placeholder: true }]);
  };

  const renameSection = (oldName, newName) => {
    if (!newName.trim() || newName === oldName) { setRenamingSection(null); return; }
    const n = newName.trim();
    save(props.map(p => (p._section || DEFAULT_SECTION) === oldName ? { ...p, _section: n } : p));
    if (activeSection === oldName) setActiveSection(n);
    setRenamingSection(null);
  };

  const deleteSection = (name) => {
    if (name === DEFAULT_SECTION) return;
    save(props.filter(p => (p._section || DEFAULT_SECTION) !== name));
    if (activeSection === name) setActiveSection(DEFAULT_SECTION);
  };

  return (
    <div data-testid="master-doc-props" className="flex-1 overflow-y-auto p-6 max-w-3xl w-full self-center">
      <div className="flex items-center gap-2 mb-4 text-xs text-[#A1A1AA]">
        <FileText className="w-3.5 h-3.5" />
        <span className="text-[#EDEDED] font-medium">{doc?.name || "Master Document"}</span>
      </div>

      <div className="border border-[#2A2A2A] rounded-md overflow-hidden">
        {/* Section tab bar */}
        <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[#1a1a1a] bg-[#0c0c0c] overflow-x-auto">
          {sectionNames.map(name => (
            <div key={name} className="group relative flex items-center flex-shrink-0">
              {renamingSection === name ? (
                <input autoFocus defaultValue={name}
                  className="text-[11px] px-2 py-0.5 rounded bg-[#1a1a1a] text-[#EDEDED] outline-none border border-[#5A90F5] w-28"
                  onBlur={e => renameSection(name, e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenamingSection(null); }} />
              ) : (
                <button onClick={() => setActiveSection(name)}
                  onDoubleClick={() => name !== DEFAULT_SECTION && setRenamingSection(name)}
                  className={`text-[11px] px-2 py-0.5 rounded transition-colors ${activeSection === name ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#A1A1AA] hover:bg-[#1a1a1a] hover:text-[#EDEDED]"}`}>
                  {name}
                </button>
              )}
              {name !== DEFAULT_SECTION && renamingSection !== name && (
                <button onClick={() => deleteSection(name)}
                  className="opacity-0 group-hover:opacity-60 hover:!opacity-100 ml-0.5 p-0.5 rounded hover:bg-[#2a2a2a] text-[#A1A1AA] flex-shrink-0">
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
          <button onClick={addSection}
            className="text-[10px] px-2 py-0.5 rounded text-[#3F3F46] hover:text-[#A1A1AA] hover:bg-[#1a1a1a] flex items-center gap-1 flex-shrink-0">
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {/* Column headers */}
        <div className="flex border-b border-[#1a1a1a] bg-[#111] text-[10px] font-semibold uppercase tracking-widest text-[#3F3F46]">
          <div className="w-2/5 flex-shrink-0 px-3 py-1.5 border-r border-[#1a1a1a] flex items-center gap-1.5">
            <Settings2 className="w-3 h-3" /> Key
          </div>
          <div className="w-24 flex-shrink-0 px-3 py-1.5 border-r border-[#1a1a1a]">Type</div>
          <div className="flex-1 px-3 py-1.5">Value</div>
        </div>

        {/* Property rows */}
        {sectionItems.filter(p => !p._placeholder).map((prop, idx) => (
          <div key={`${prop.key}-${idx}`}
            className="group flex border-b border-[#1a1a1a] last:border-b-0 min-h-[34px] bg-[#0f0f0f] hover:bg-[#141414] transition-colors"
            onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, prop, idx); }}>
            <div className="w-2/5 flex-shrink-0 px-3 flex items-center border-r border-[#1a1a1a] min-w-0">
              <span className="text-[12px] text-[#EDEDED] font-medium truncate flex-1" title={prop.key}>{prop.key}</span>
            </div>
            <div className="w-32 flex-shrink-0 px-2 flex items-center border-r border-[#1a1a1a]">
              <div className="relative w-full flex items-center gap-1">
<select value={prop.type || "text"} onChange={e => setVal(idx, { type: e.target.value, value: "" })}
                  className="flex-1 bg-transparent text-[10px] text-[#52525B] hover:text-[#A1A1AA] outline-none cursor-pointer appearance-none">
                  {PROP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown className="w-2.5 h-2.5 flex-shrink-0 text-[#3F3F46] pointer-events-none" />
              </div>
            </div>
            <div className="flex-1 px-3 flex items-center min-w-0">
              <ValueCell
                key={`vc§${prop.key}§${prop.value}§${activeSection}`}
                initialValue={prop.value}
                propType={prop.type || "text"}
                onCommit={v => setVal(idx, { value: v })} />
            </div>
            <div className="w-8 flex-shrink-0 flex items-center justify-center border-l border-[#1a1a1a]">
              <button onClick={() => remove(idx)}
                className="opacity-0 group-hover:opacity-60 hover:!opacity-100 p-1 rounded hover:bg-[#1a1a1a] text-[#FF3B30] transition-opacity"
                title="Delete property">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}

        {/* Add row — includes value input on init */}
        {adding && (
          <div className="flex border-b border-[#1a1a1a] bg-[#0a0a0a] min-h-[34px]">
            <div className="w-2/5 flex-shrink-0 px-3 flex items-center border-r border-[#1a1a1a]">
              <input ref={keyRef} value={newKey} onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setAdding(false); setNewKey(""); } }}
                placeholder="Property name…"
                className="flex-1 bg-transparent text-[12px] text-[#EDEDED] outline-none placeholder-[#3F3F46] min-w-0" />
            </div>
            <div className="w-32 flex-shrink-0 px-2 flex items-center border-r border-[#1a1a1a]">
              <div className="relative w-full">
                <select value={newType} onChange={e => { setNewType(e.target.value); setNewValue(""); }}
                  className="w-full bg-transparent text-[10px] text-[#52525B] outline-none cursor-pointer appearance-none pr-3">
                  {PROP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown className="w-2.5 h-2.5 absolute right-0 top-1/2 -translate-y-1/2 text-[#3F3F46] pointer-events-none" />
              </div>
            </div>
            <div className="flex-1 px-3 flex items-center gap-2">
              <NewValueInput propType={newType} value={newValue} onChange={setNewValue} />
              <button onClick={() => { setAdding(false); setNewKey(""); setNewValue(""); }}
                className="text-[10px] text-[#52525B] hover:text-[#A1A1AA] px-2 py-0.5 rounded flex-shrink-0">Cancel</button>
              <button onClick={commit}
                className="text-[10px] text-[#5A90F5] px-2 py-0.5 rounded hover:bg-[#5A90F5]/10 flex-shrink-0">Add</button>
            </div>
          </div>
        )}

        <button onClick={() => { setAdding(true); setTimeout(() => keyRef.current?.focus(), 50); }}
          className="w-full px-3 py-2.5 flex items-center gap-2 text-[11px] text-[#A1A1AA] bg-[#0f0f0f] hover:bg-[#141414] hover:text-[#EDEDED] transition-colors border-t border-[#1a1a1a]">
          <Plus className="w-3.5 h-3.5" /> Add property to {activeSection}
        </button>
      </div>
    </div>
  );
}
